import React, { useState, useEffect } from 'react';
import { Icons as I } from '../pack-icons';

const STORAGE_KEY = 'dathent-theme';

/**
 * ThemeToggle — 2 inline buttons (light / dark).
 *
 * Previously offered a 3rd "system" option (monitor icon) that mirrored
 * prefers-color-scheme. That was removed: marketing + product agreed
 * the monitor icon read as a glyph that didn't belong in a brand
 * header, and the matchMedia subscription was overkill for an explicit
 * theme picker. Existing users whose localStorage carries 'system' get
 * gracefully migrated on first read (resolved to their current OS
 * preference's concrete light/dark, then persisted as that).
 */
export default function ThemeToggle() {
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    if (stored === 'system') {
      // Migration: resolve once to a concrete theme based on OS pref.
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    // Default to current DOM state (set by FOWT script), or 'light'.
    return document.documentElement.dataset.theme || 'light';
  });

  // Apply theme + persist on mode change.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    document.documentElement.style.colorScheme = mode;
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const opts = [
    { id: 'light', Icon: I.sun },
    { id: 'dark', Icon: I.moon },
  ];

  return (
    <div className="tt" role="group" aria-label="Theme">
      {opts.map((o) => (
        <button
          key={o.id}
          className={mode === o.id ? 'is-active' : ''}
          onClick={() => setMode(o.id)}
          aria-label={o.id}
          type="button"
        >
          <o.Icon size={14} />
        </button>
      ))}
    </div>
  );
}
